/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { Item } from '../utils/UNSAFE_dataProvider';
import * as Logger from '../utils/UNSAFE_logger';
import { playAudioFromURL, playDefaultNotificationSound } from '../utils/UNSAFE_soundUtils';
import { isValidValueForProp } from './MessageFormattingUtils';
import { MessageSeverity, MessageVariant } from './Message.types';

/**
 * Logger that prepends the component name to the message
 */
const MessageLogger = {
  error: (message: string, type = 'common') => Logger.error(`JET Message(${type}): ${message}`),
  warn: (message: string, type = 'common') => Logger.warn(`JET Message(${type}): ${message}`),
  info: (message: string, type = 'common') => Logger.info(`JET Message(${type}): ${message}`),
  log: (message: string, type = 'common') => Logger.log(`JET Message(${type}): ${message}`)
};

/**
 * Plays a sound based on the provided argument. Supported keywords:
 * 1. default - plays the default beep sound
 * 2. none - no sound will be played
 *
 * @param sound Supported keywords or URL to an audio file
 */
async function playSound(sound: string) {
  if (sound === 'none') {
    // no need to play any audio
    return;
  }

  // For default, we play a beep sound using WebAudio API
  if (sound === 'default') {
    try {
      playDefaultNotificationSound();
    } catch (error) {
      // Default sound is not played due to some error
      // Log a message and return doing nothing else
      MessageLogger.warn(`Failed to play the default sound. ${error}.`);
    }
    return;
  }

  // If it is not a key word, then it is an URL
  try {
    await playAudioFromURL(sound);
  } catch (error) {
    // Playing audio using the URL failed.
    MessageLogger.warn(`Failed to play the audio from the url ${sound}. ${error}.`);
  }
}

/**
 * A helper function that throws an error
 *
 * @param message The error message
 * @param type The type of the message that is throwing an error
 * @throws {Error}
 */
function throwError(message: string, type = 'common') {
  throw new Error(`JET Message(${type}) - ${message}`);
}

/**
 * Fetches a renderer for the current message if one is provided
 *
 * @param message The item context for the current message
 * @param rendererIdentifier Identifier of the current renderer
 * @param renderers All available renderers
 * @returns The renderer for rendering the custom content
 */
function getRenderer<K, D, R>(
  message: Item<K, D>,
  rendererIdentifier?: string | ((item: Item<K, D>) => string | undefined),
  renderers?: Record<string, (data: R) => ComponentChildren>,
  type?: string
) {
  // If either detailRenderer function or record of renderers are not available,
  // return null
  if (!rendererIdentifier || !renderers) {
    return undefined;
  }

  const rendererKey =
    typeof rendererIdentifier === 'function' ? rendererIdentifier(message) : rendererIdentifier;

  // If rendererKey is null or undefined, then we need to use default rendering
  // so return null
  if (rendererKey == null) {
    return undefined;
  }

  // If the returned render key is a string but does not exist in the provided
  // record of renderers, throw an error
  if (!(rendererKey in renderers)) {
    throwError(
      `${rendererKey} is not a valid template name for the message with key=${message.key}`,
      type
    );
  }

  // Else, fetch and return the renderer
  return renderers[rendererKey];
}

/**
 * Generates a root style class based on the severity. For invalid severity and severity=none
 * no specific style class exists.
 *
 * @param severity The message severity
 * @returns calculated style class based on the severity
 */
function severityBasedStyleClass(severity: MessageSeverity, variant: MessageVariant) {
  const isValidSeverity = isValidValueForProp(severity, 'severity');
  return isValidSeverity && severity !== 'none' ? `oj-c-message${variant}-${severity}` : '';
}

/**
 * Determines if a severity icon is needed based on the component severity
 *
 * @param severity The component severity
 * @returns Whether or not to render the severity icon
 */
function isSeverityIconNeeded(
  severity: MessageSeverity
): severity is Exclude<MessageSeverity, 'none'> {
  const isValidSeverity = isValidValueForProp(severity, 'severity');
  return isValidSeverity && severity !== 'none';
}

export { getRenderer, playSound, throwError, severityBasedStyleClass, isSeverityIconNeeded };
